import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, DefaultPrivacyLevel, type RumInitConfiguration } from '@datadog/browser-rum';
import { addReactError, reactPlugin } from '@datadog/browser-rum-react';

import type { LoggerOptions } from 'types/Client';

import { TestingCommands } from 'enums/testing';
import { getReleaseIdentifier } from 'utils/deviceInfoUtils';
import { APIType, getGraphURL } from 'utils/graphUrl';
import { isLoggingEnabled } from 'utils/loggingUtils';
import { getTelemetryEnvName, isCypressCI } from 'utils/runtimeUtils';
import { isStorybook } from 'utils/storybookUtils';

import type { User } from 'store/client/graphql';

import type LoggingService from './LoggingService';

// eslint-disable-next-line unicorn/no-static-only-class
class DatadogLogger {
  static LoggingService: typeof LoggingService;

  /**
   * Record a feature-flag evaluation on the current RUM session/view.
   * Datadog stores it under `@feature_flags.<key>`, so RUM events (errors, resources,
   * views) can be filtered and correlated by flag state. Wired from LaunchDarkly's
   * `flag-used` inspector (see App.tsx) so every evaluated flag is captured automatically.
   *
   * @param key The LaunchDarkly flag key (e.g. `dashboardEnabled`)
   * @param value The evaluated flag value (booleans in this app; kept as `unknown` for safety)
   * @example DatadogLogger.addFeatureFlagEvaluation('dashboardEnabled', true)
   */
  static addFeatureFlagEvaluation(key: string, value: unknown) {
    if (!DatadogLogger.isEnabled()) {
      return;
    }

    datadogRum.addFeatureFlagEvaluation(key, value);
  }

  /**
   * Capture Error Exception
   * Useful for manually logging an error event in DD UI without invoking error boundary.
   *
   * @param error
   * @param info
   * @example DatadogLogger.captureException(new Error('Error'), { scope: 'error-boundary' })
   */
  static captureException(error: Error, info: Record<string, any>) {
    if (!DatadogLogger.isEnabled()) {
      return;
    }

    datadogRum.addError(error, info);
  }

  /**
   * Report a React rendering error caught by a React error boundary to Datadog RUM.
   * Uses `addReactError` from `@datadog/browser-rum-react` so the error is recorded with the
   * React component stack, unlike a plain action/breadcrumb.
   *
   * @param error The error thrown during render
   * @param info React error info (e.g. `{ componentStack }`) from `componentDidCatch`
   * @example DatadogLogger.captureReactError(error, errorInfo)
   */
  static captureReactError(error: Error, info: Record<string, any>) {
    if (!DatadogLogger.isEnabled()) {
      return;
    }

    addReactError(error, info);
  }

  static clearUser() {
    if (!DatadogLogger.isEnabled()) {
      return;
    }

    datadogRum.clearUser();
  }

  static init(_LogginService: typeof LoggingService) {
    if (!DatadogLogger.isEnabled()) {
      return;
    }

    this.LoggingService = _LogginService;

    const commonInitProperties = {
      applicationId: '',
      clientToken: process.env.REACT_APP_FEATURE_DATADOG_RUM_CLIENT_TOKEN || '',
      env: getTelemetryEnvName(),
      service: process.env.REACT_APP_FEATURE_DATADOG_RUM_SERVICE || '',
      sessionSampleRate: 100,
      site: 'datadoghq.com',
      telemetrySampleRate: 0,
      version: getReleaseIdentifier(),
    } satisfies RumInitConfiguration;

    /*
     * The resolved GraphQL endpoint. Used to enrich each GraphQL RUM resource with its
     * operation name (allowedGraphQlUrls below). getGraphURL resolves the debug-mode URL
     * too, unlike the bare env var; RUM is prod-only so both agree in practice.
     */
    const graphqlUrl = getGraphURL(APIType.GRAPHQL);

    datadogRum.init({
      ...commonInitProperties,
      /**
       * Datadog's React integration. `router: false` because our custom `pathPattern` route
       * view-naming (see RouterRoutes.tsx) covers view tracking — Datadog's router
       * auto-instrumentation only supports react-router v6/v7, and this app is on v8.
       * The plugin still powers React error reporting via `addReactError` (captureReactError).
       *
       * Load-bearing invariant: do NOT set `trackViewsManually` on this init. RUM must stay
       * in automatic view mode so the SDK creates a view per navigation (initial_load +
       * route_change) and attributes its Core Web Vitals (LCP/FCP) to it; `trackPageView`
       * below only renames that auto-created view via `setViewName`. Enabling manual tracking
       * would make `setViewName` rename the initial view forever and drop LCP again — silently,
       * with no failing test.
       */
      plugins: [reactPlugin({ router: false })],
      /*
       * Enrich the GraphQL POST endpoint so each RUM resource carries the operation name
       * (@resource.graphql.operation_name) instead of appearing as an opaque `POST /graphql`.
       * This unlocks per-operation latency/error slicing in RUM. Match is EXACT string
       * equality (not a prefix), so pass the resolved URL — never ''. Fail closed: only
       * register the option when the URL is defined (mirrors the allowedTracingUrls guard),
       * which also avoids an empty-string exact match that would silently match nothing.
       *
       * trackResponseErrors is DELIBERATELY OMITTED: it captures resource.graphql.errors[]
       * message strings verbatim, and that path is NOT in the RESOURCE beforeSend allowlist
       * (only resource.graphql.variables is), so backend error messages cannot be redacted
       * client-side. Do not enable it until the API guarantees error messages never echo
       * user input. trackPayload is likewise omitted (it would capture the raw query text).
       */
      ...(graphqlUrl && { allowedGraphQlUrls: [{ match: graphqlUrl }] }),
      /*
       * Redact GraphQL variables before any RUM event leaves the browser. Once a URL matches
       * allowedGraphQlUrls, the SDK captures resource.graphql.variables AUTOMATICALLY, and
       * this app's variables carry customer/financing PII.
       *
       * Assign an EMPTY STRING, never undefined/null. The SDK runs beforeSend on a clone and
       * copies a field back only if the new value's runtime type matches the declared type;
       * resource.graphql.variables is declared "string", so `undefined` is silently discarded
       * and the original unredacted variables would ship. '' passes the check and is written.
       */
      beforeSend: event => {
        /*
         * Discard benign fetch-cancellation errors. Apollo aborts in-flight GraphQL
         * requests when a component unmounts or the user navigates away mid-query
         * (heaviest on query-dense views like /retail/:id and /dashboard), and the SDK
         * surfaces the resulting AbortError as an unhandled error with an empty stack.
         * It is expected cancellation with no user impact, so it is error-tracking noise
         * rather than a real failure. Match on error.type, which is browser-stable, not
         * the localized message ("The user aborted a request." varies across browsers).
         */
        if (event.type === 'error' && event.error.type === 'AbortError') {
          return false;
        }

        if (event.type === 'resource' && event.resource.graphql?.variables !== undefined) {
          event.resource.graphql.variables = '';
        }

        return true;
      },
      /**
       * Enable allowUntrustedEvents for Cypress CI tests to correctly capture programmatic clicks.
       * Required for RUM session linking with CI Visibility.
       * @see https://docs.datadoghq.com/tests/browser_tests/
       */
      allowedTracingUrls: [window.location.origin, process.env.REACT_APP_GRAPH_BASE]
        .filter(url => url !== undefined)
        .map(String),
      allowUntrustedEvents: DatadogLogger.isCypressCITest(),
      applicationId: process.env.REACT_APP_FEATURE_DATADOG_RUM_APPLICATION_ID || '',
      /*
       * Sample 10% of sessions for Session Replay to reduce Datadog cost. RUM
       * Sessions stay at 100% (sessionSampleRate above), so monitoring/error/perf
       * data is unaffected — only the screen recordings are sampled down. Light
       * usage (occasional bug triage / user-journey review); revisit if error-triage
       * replays start coming up missing.
       */
      sessionReplaySampleRate: 10,
      /**
       * All user input is not sensitive, we can track all for better monitoring and
       * explicitly mask certain fields.
       * Search for `DD_PRIVACY_HTML_ATTRIBUTE` to see the specific fields that are masked.
       *
       * More info: https://docs.datadoghq.com/real_user_monitoring/session_replay/privacy_options/#privacy-options
       */
      defaultPrivacyLevel: DefaultPrivacyLevel.ALLOW,
      startSessionReplayRecordingManually: true,
      trackUserInteractions: true,
    });
    datadogRum.setGlobalContextProperty('cypress', !!window[TestingCommands.CYPRESS]);
    datadogRum.setGlobalContextProperty('storybook', isStorybook());

    datadogLogs.init({
      ...commonInitProperties,

      forwardConsoleLogs: 'all',
      forwardErrorsToLogs: true,
      forwardReports: 'all',
    });
    datadogLogs.setGlobalContextProperty('cypress', !!window[TestingCommands.CYPRESS]);
    datadogLogs.setGlobalContextProperty('storybook', isStorybook());
  }

  /**
   * Checks if running Cypress in CI environment.
   * When true, RUM is enabled to allow session replay linking with CI Visibility.
   *
   * Note: Only checks the env var since window.Cypress isn't set at app init time.
   * Explicitly checks for 'true' string since env vars are always strings.
   */
  static isCypressCITest(): boolean {
    return isCypressCI();
  }

  static isEnabled(): boolean {
    const hasRequiredDatadogConfig =
      !!process.env.REACT_APP_FEATURE_DATADOG_RUM_APPLICATION_ID &&
      !!process.env.REACT_APP_FEATURE_DATADOG_RUM_CLIENT_TOKEN &&
      !!process.env.REACT_APP_FEATURE_DATADOG_RUM_SERVICE &&
      !!process.env.REACT_APP_FEATURE_DATADOG_ENV_NAME &&
      !isStorybook();

    /**
     * For Cypress CI tests, bypass isLoggingEnabled() since those runs are served from
     * localhost and would be gated out, but we need RUM for session linking with CI
     * Visibility.
     */
    if (DatadogLogger.isCypressCITest()) {
      return hasRequiredDatadogConfig;
    }

    return hasRequiredDatadogConfig && isLoggingEnabled() && !window[TestingCommands.CYPRESS];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static log(options: LoggerOptions) {
    // TODO: (ED-8261) Potentially enable custom logger (remove `true` clause)
    // eslint-disable-next-line no-constant-condition
    if (!DatadogLogger.isEnabled() || true) {
      return;
    }
    /*
     *Const { message, messageContext, status, error } = options;
     * // Need to send object as other types would silently fail e.g. arrays
     *const messageContextObject: Record<string, any> = { messageContext };
     *
     * // Stringify for debug comparisons and ensure data available to debug issue. Can remove in future.
     *if (messageContext) {
     *  messageContextObject.messageContextStr = JSON.stringify(
     *    messageContext,
     *    Object.getOwnPropertyNames(messageContext)
     *  );
     *}
     *if (error) {
     *  messageContextObject.errorStr = JSON.stringify(error, Object.getOwnPropertyNames(error));
     *}
     *
     *datadogLogs.logger.log(message, messageContextObject, status, error);
     */
  }

  static setUser({ displayName, group, ...props }: Pick<User, 'displayName' | 'email' | 'group' | 'id' | 'scope'>) {
    if (!DatadogLogger.isEnabled()) {
      return;
    }

    datadogRum.setUser({
      group: group?.name.value,
      name: displayName,
      ...props,
    });
  }

  static startSessionReplayRecording() {
    if (!DatadogLogger.isEnabled()) {
      return;
    }

    datadogRum.startSessionReplayRecording();
  }

  /**
   * Stops a session replay recording
   *
   * @example Manually stop recording. Also automatically handled by DD.
   */
  static stopSessionReplayRecording() {
    if (!DatadogLogger.isEnabled()) {
      return;
    }

    datadogRum.stopSessionReplayRecording();
    datadogRum.stopSession();
  }

  /**
   * Track user interactions and/or custom events.
   * Will be seen in datadog `Sessions` UI.
   *
   * @param action
   * @param context
   * @example DatadogLogger.trackAction('CLICK', { id: 'someEventId' })
   */
  static trackAction(action: string, context?: Record<string, any>) {
    if (!DatadogLogger.isEnabled()) {
      return;
    }

    this.LoggingService?.log({
      message: `Custom Action: ${action}`,
      messageContext: { ...context },
    });
    datadogRum.addAction(action, context);
  }

  /**
   * Rename the CURRENT RUM view to our route pattern (e.g. `/retail/:id`).
   *
   * Uses `setViewName`, NOT `startView`, deliberately. RUM runs in automatic
   * view-tracking mode (no `trackViewsManually`), so the SDK already creates the
   * initial-load view at page load and attributes the initial-load Core Web Vitals
   * (LCP/FCP/loading_time) to it. `startView` would END that view and START a fresh
   * one from CoreRoute's post-mount effect — after first paint — which drops LCP
   * entirely and double-counts every route change. `setViewName` renames the SDK's
   * current view in place, preserving its id, `initial_load` type, and timings.
   *
   * @param path Route pattern to name the current view. Empty patterns are ignored
   *   (e.g. the 404 catch-all) so we never blank out the SDK's URL-derived name.
   * @example DatadogLogger.trackPageView('/my-custom-page-view')
   */
  static trackPageView(path: string) {
    if (!path || !DatadogLogger.isEnabled()) {
      return;
    }

    datadogRum.setViewName(path);
  }
}

export default DatadogLogger;
